import { BaseError } from "@/domains/errors";
import { api } from "@/modules/api";
import { objectModule } from "@/modules/object";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { windowModule } from "@/modules/window";
import { buildAppWindowUrl } from "@/modules/window/build-url";
import { PlatformAccountState } from "@/store/platform-info/types";
import { logger } from "@/modules/logger";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import { GatedFeature, GateUsage } from "@/store/feature-gates/types";
import { routingModule } from "@/modules/routing";

export class SettingsSubscriptionManagementPageStore extends AppSubStore {
  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable(this, {
      fetchStripeCheckoutSession: action,
      hasActiveSubscription: computed,
      openBillingPortal: action,
      openSubscriptionModal: action,
      fetchBillingPortalUrl: action,
      refreshUsage: action,
      notesUsage: observable,
      chatUsage: observable,
      isUsageLoading: observable,
    });
  }

  get hasActiveSubscription() {
    return this.store.platformInfo.accountState === PlatformAccountState.Active;
  }

  notesUsage: GateUsage | undefined = undefined;
  chatUsage: GateUsage | undefined = undefined;
  isUsageLoading = false;

  async refreshUsage(): Promise<void> {
    this.isUsageLoading = true;
    try {
      const [notes, chat] = await Promise.all([
        this.store.featureGates.checkGate(GatedFeature.NoteCreate),
        this.store.featureGates.checkGate(GatedFeature.ChatMessageSend),
      ]);

      runInAction(() => {
        this.notesUsage = notes;
        this.chatUsage = chat;
      });
    } finally {
      runInAction(() => {
        this.isUsageLoading = false;
      });
    }
  }

  public async openBillingPortal() {
    try {
      const billingPortalUrl = await this.fetchBillingPortalUrl();
      windowModule.navigateToExternal({ url: new URL(billingPortalUrl) });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.error(err);

      alert("Unable to contact Stripe. Please try again later, or contact support@mem.ai");
    }
  }

  public openSubscriptionModal() {
    this.store.platformInfo.openSubscribeNowInterstitial();
  }

  public async fetchBillingPortalUrl(): Promise<string> {
    const spaceId = this.store.spaces.myPersonalSpaceId;
    const response = await api.get("/v2/me/subscriptions/stripe/billing-portal", {
      params: { query: { space_id: spaceId } },
    });

    if (response.data) {
      return response.data.stripe_billing_portal_url;
    }

    const err = new BaseError({
      message:
        "[SettingsSubscriptionManagementPageStore] Failed to fetch billing portal url for current account.",
      info: { result: objectModule.safeAsJson(response) },
    });

    logger.error(err);

    throw err;
  }

  public async fetchStripeCheckoutSession(
    billingStrategy: BillingStrategyEnum
  ): Promise<string | null> {
    // For callers that still use this method, keep behavior stable by using current path
    const pathname = routingModule.parsePathname(this.store.navigation.activePath);
    const search = this.store.navigation.activeSearchQuery || "";
    const baseUrl = buildAppWindowUrl({ path: pathname + search }).toString();
    const separator = search ? "&" : "?";
    const successUrl = `${baseUrl}${separator}stripe_success=1&session_id={CHECKOUT_SESSION_ID}`;

    // TODO (Rick, Aug 11): move this API call to a store
    const response = await api.post("/v2/me/subscriptions/stripe/checkout-session", {
      body: {
        cancel_url: baseUrl,
        success_url: successUrl,
        billing_strategy: billingStrategy,
      },
    });

    if (response.data?.stripe_checkout_session_url) {
      return response.data.stripe_checkout_session_url;
    } else {
      return null;
    }
  }
}
